/**
 * Feature #175 / FS175 — Macro library: dashboard data layer.
 *
 * Thin typed wrappers around `/api/v1/macro-library/(tokens|preview|validate)`
 * plus pure helpers shared with the page + tests.
 *
 * The API layer is a pure, read-only catalogue — every request is idempotent.
 * The playground builds its context client-side from the input widgets and
 * POSTs a single-shot preview request on every interaction.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types (mirror the API lib — intentionally duplicated so dash stays
// independent of api/ imports.)
// ---------------------------------------------------------------------------

export const MACRO_CATEGORIES = [
	"click",
	"campaign",
	"publisher",
	"advertiser",
	"device",
	"geo",
	"locale",
	"network",
	"request",
	"creative",
	"conversion",
	"time",
	"sub",
	"custom",
] as const;
export type MacroCategory = (typeof MACRO_CATEGORIES)[number];

export const MACRO_CATEGORY_LABEL: Record<MacroCategory, string> = {
	click: "Click",
	campaign: "Campaign",
	publisher: "Publisher",
	advertiser: "Advertiser",
	device: "Device",
	geo: "Geography",
	locale: "Locale",
	network: "Network",
	request: "Request",
	creative: "Creative",
	conversion: "Conversion",
	time: "Time",
	sub: "Sub IDs",
	custom: "Custom",
};

export const MACRO_CATEGORY_HINT: Record<MacroCategory, string> = {
	click: "Identifies the click event itself.",
	campaign: "Attributes the event to a campaign.",
	publisher: "Identifies the publisher that drove the traffic.",
	advertiser: "Identifies the advertiser receiving the traffic.",
	device: "Client device, OS, and browser metadata at click time.",
	geo: "Country, region, and city where the click came from.",
	locale: "Language preferences reported by the browser.",
	network: "Carrier / ISP metadata, if we could detect it.",
	request: "Privacy-safe hashes of request metadata.",
	creative: "The creative rendered for the click.",
	conversion: "Reported conversion outcome, revenue, and currency.",
	time: "Event timestamps in Unix seconds and ISO-8601.",
	sub: "Ten free-form publisher passthrough slots (sub1..sub10).",
	custom: "Tenant-defined tokens referenced as {custom.<name>}.",
};

export interface MacroTokenSpec {
	token: string;
	category: MacroCategory;
	description: string;
	example: string;
}

export interface MacroListResponse {
	count: number;
	tokens: MacroTokenSpec[];
}

export interface ValidationIssue {
	code: string;
	message: string;
	detail?: string;
}

export interface ValidationResponse {
	valid: boolean;
	issues: ValidationIssue[];
	warnings: ValidationIssue[];
	placeholders: string[];
	unknown_placeholders: string[];
}

export interface PreviewResponse {
	template: string;
	output: string;
	valid: boolean;
	issues: ValidationIssue[];
	warnings: ValidationIssue[];
	placeholders: string[];
	substituted: string[];
	empty: string[];
	unknown: string[];
}

/** Must stay in lockstep with the MacroContext on the API side. */
export interface MacroContext {
	click_id?: string;
	campaign_id?: string;
	campaign_name?: string;
	publisher_id?: string;
	publisher_name?: string;
	advertiser_id?: string;
	device_type?: string;
	os?: string;
	os_version?: string;
	browser?: string;
	browser_version?: string;
	country?: string;
	region?: string;
	city?: string;
	language?: string;
	carrier?: string;
	ip_hash?: string;
	ua_hash?: string;
	creative_id?: string;
	goal?: string;
	revenue_cents?: string | number;
	currency?: string;
	ts?: string | number;
	ts_iso?: string;
	sub?: Array<string | null | undefined>;
	custom?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Pure helpers (also used by tests)
// ---------------------------------------------------------------------------

const PLACEHOLDER_RE = /\{([a-zA-Z_][a-zA-Z0-9_.]*)\}/g;
const CUSTOM_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;
const BASE_TOKEN_RE = /^[a-z][a-z0-9_]{0,63}$/;

export function extractPlaceholders(template: string): string[] {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const m of template.matchAll(PLACEHOLDER_RE)) {
		const name = m[1];
		if (!name || seen.has(name)) continue;
		seen.add(name);
		out.push(name);
	}
	return out;
}

export function isCustomPlaceholder(name: string): boolean {
	if (!name.startsWith("custom.")) return false;
	const rest = name.slice("custom.".length);
	return CUSTOM_NAME_RE.test(rest);
}

export function isBaseTokenShape(name: string): boolean {
	return BASE_TOKEN_RE.test(name);
}

export function groupTokensByCategory(
	rows: MacroTokenSpec[],
): Array<{ category: MacroCategory; tokens: MacroTokenSpec[] }> {
	const bucket = new Map<MacroCategory, MacroTokenSpec[]>();
	for (const row of rows) {
		const list = bucket.get(row.category);
		if (list) list.push(row);
		else bucket.set(row.category, [row]);
	}
	const ordered: Array<{ category: MacroCategory; tokens: MacroTokenSpec[] }> =
		[];
	for (const cat of MACRO_CATEGORIES) {
		const tokens = bucket.get(cat);
		if (tokens && tokens.length > 0) ordered.push({ category: cat, tokens });
	}
	return ordered;
}

export function renderEncodedSnippet(token: string): string {
	return encodeURIComponent(`{${token}}`);
}

/**
 * Given a free-form `{key=value;key2=value2}` string, parse it into a
 * sparse MacroContext. Used by the playground so operators can paste a
 * quick sample without filling every field by hand.
 */
export function parseContextShorthand(input: string): {
	context: MacroContext;
	unknown: string[];
} {
	const context: MacroContext = {};
	const unknown: string[] = [];
	if (!input.trim()) return { context, unknown };

	const parts = input.split(";").map((s) => s.trim()).filter(Boolean);
	const customBucket: Record<string, string> = {};
	const subBucket: Array<string | undefined> = [];
	for (const part of parts) {
		const eq = part.indexOf("=");
		if (eq === -1) continue;
		const key = part.slice(0, eq).trim();
		const value = part.slice(eq + 1).trim();
		if (!key) continue;

		if (key.startsWith("custom.")) {
			const rest = key.slice("custom.".length);
			if (CUSTOM_NAME_RE.test(rest)) {
				customBucket[rest] = value;
			} else {
				unknown.push(key);
			}
			continue;
		}
		const subMatch = key.match(/^sub([1-9]|10)$/);
		if (subMatch) {
			const idx = parseInt(subMatch[1]!, 10) - 1;
			while (subBucket.length <= idx) subBucket.push(undefined);
			subBucket[idx] = value;
			continue;
		}
		if (!BASE_TOKEN_RE.test(key)) {
			unknown.push(key);
			continue;
		}
		(context as unknown as Record<string, unknown>)[key] = value;
	}
	if (subBucket.length > 0) context.sub = subBucket;
	if (Object.keys(customBucket).length > 0) context.custom = customBucket;
	return { context, unknown };
}

// ---------------------------------------------------------------------------
// HTTP wrappers
// ---------------------------------------------------------------------------

export async function fetchMacroTokens(): Promise<MacroTokenSpec[]> {
	try {
		const res = await apiClient.get<MacroListResponse>(
			"/macro-library/tokens",
		);
		return res?.tokens ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

export async function previewTemplate(
	template: string,
	context?: MacroContext,
): Promise<PreviewResponse> {
	const body: Record<string, unknown> = { template };
	if (context !== undefined) body.context = context;
	return apiClient.post<PreviewResponse>("/macro-library/preview", body);
}

export async function validateTemplate(
	template: string,
): Promise<ValidationResponse> {
	return apiClient.post<ValidationResponse>("/macro-library/validate", {
		template,
	});
}

// ---------------------------------------------------------------------------
// Stable paths — exported for doc hints and tests
// ---------------------------------------------------------------------------

export const macroLibraryPaths = {
	tokens: "/macro-library/tokens",
	preview: "/macro-library/preview",
	validate: "/macro-library/validate",
} as const;
